import {useMemo, useState} from 'react';
import ReactSelect from 'react-select';
import {BillCategory, BillSearchResponse} from '../../interfaces/bill';
import {formatAmount} from '../../common/tools/amount';
import {ColumnDef} from '@tanstack/react-table';
import {useQuery, useMutation} from 'react-query';
import {formatDate} from '../../common/tools/date';
import {getBillCategory, getDueDateColor} from '../../common/tools/bill';
import {OverlayTrigger, Tooltip, Modal, Form, Spinner } from 'react-bootstrap';
import {createSearchParams, useNavigate} from 'react-router-dom';
import DownloadBill from '../dashboard/last-bills/DownloadBill';
import EtatBadge from '../../../Domains/Service/Components/EtatBadge';
import TableMetronic from '../../common/components/table/TableMetronic';
import {useIntl} from 'react-intl';
import {getBills} from './api';
import { saveAs } from 'file-saver';
import axios from 'axios';
import {downloadFile} from '../documents/api';
import Loading from '../../common/components/Loading';
import styles from './MyBills.module.scss';
import clsx from 'clsx';

const THEME_COLORS: Record<string, string> = {
  'celeste-primary': '#5b70b0',
  success: '#5da170',
  warning: '#de8100',
  danger: '#c44d4d',
  yellow: '#e6c84a',
  dark: '#181c32',
};

const themeColor = (name: string): string => THEME_COLORS[name] ?? name;

type TypeFilter = 'all' | BillCategory.BILL | BillCategory.CREDIT;
type StatusFilter = 'all' | 'paid' | 'unpaid';

interface FilterOption<T> {
  value: T;
  label: string;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const billStatus = (
  bill: BillSearchResponse,
  paidLabel: string,
  unpaidLabel: string
): {label: string; color: string} => {
  if (bill.isInvoiceOpen === null) {
    return {label: '—', color: '#ababab'};
  }
  return bill.isInvoiceOpen
    ? {label: unpaidLabel, color: '#de8100'}
    : {label: paidLabel, color: '#5da170'};
};

const BillMobileActions = ({bill}: {bill: BillSearchResponse}): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  return (
    <div className={styles.mobileCardActions} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={clsx('btn btn-sm btn-light-primary', styles.mobileActionButton)}
        disabled={isDownloading}
        onClick={() => {
          setIsDownloading(true);
          downloadFile(`${axios.defaults.baseURL}/api/billing/bills/${bill.id}/pdf`).finally(() =>
            setIsDownloading(false)
          );
        }}
      >
        {isDownloading ? (
          <Loading className="d-inline" />
        ) : (
          <>
            <i className="fa-solid fa-download fs-4 me-2"></i>
            {intl.formatMessage({id: 'ADMINISTRATIVE.BILL.DOWNLOAD'})}
          </>
        )}
      </button>
      <button
        type="button"
        className={clsx('btn btn-sm btn-light-danger', styles.mobileActionButton)}
        onClick={() =>
          navigate({
            pathname: `/support`,
            search: createSearchParams({bill: bill.id}).toString(),
          })
        }
      >
        <i className="fa-solid fa-file-circle-exclamation fs-4 me-2"></i>
        {intl.formatMessage({id: 'BILLS.ACTION.MAKE_REQUEST'})}
      </button>
    </div>
  );
};

const MyBills = (): JSX.Element => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const {isLoading, isError, data: bills} = useQuery<BillSearchResponse[]>(['get-bills'], getBills);

  const paidLabel = intl.formatMessage({id: 'DASHBOARD.BILLS.STATUS.PAID'});
  const unpaidLabel = intl.formatMessage({id: 'DASHBOARD.BILLS.STATUS.UNPAID'});

  const typeOptions: FilterOption<TypeFilter>[] = [
    {value: 'all', label: intl.formatMessage({id: 'BILLS.FILTER.ALL_TYPES'})},
    {value: BillCategory.BILL, label: intl.formatMessage({id: 'BILLS.TYPE.INVOICE'})},
    {value: BillCategory.CREDIT, label: intl.formatMessage({id: 'BILLS.TYPE.CREDIT'})},
  ];

  const statusOptions: FilterOption<StatusFilter>[] = [
    {value: 'all', label: intl.formatMessage({id: 'BILLS.FILTER.ALL_STATUS'})},
    {value: 'paid', label: paidLabel},
    {value: 'unpaid', label: unpaidLabel},
  ];

  const filteredBills = useMemo(() => {
    const list = bills ?? [];
    if (typeFilter === 'all' && statusFilter === 'all') return list;

    return list.filter((bill) => {
      if (typeFilter !== 'all') {
        const isCredit = bill.type === BillCategory.CREDIT;
        if (typeFilter === BillCategory.CREDIT && !isCredit) return false;
        if (typeFilter === BillCategory.BILL && isCredit) return false;
      }

      if (statusFilter === 'paid' && bill.isInvoiceOpen !== false) return false;
      if (statusFilter === 'unpaid' && bill.isInvoiceOpen !== true) return false;

      return true;
    });
  }, [bills, typeFilter, statusFilter]);
  // const [isDownloading, setIsDownloading] = useState(false);


  const columns = useMemo<ColumnDef<BillSearchResponse>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'DASHBOARD.ORDERS.COL.DATE'}),
        accessorKey: 'date',
        cell: (info) => formatDate(info.row.original.date),
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.BILLS.COL.NUMBER'}),
        accessorKey: 'number',
        cell: (info) => info.row.original.number,
      },
      {
        header: intl.formatMessage({id: 'BILLS.COL.TYPE'}),
        accessorKey: 'category',
        cell: (info) => {
          const color =
            info.row.original.type === BillCategory.BILL
              ? 'celeste-primary'
              : 'success';
          const label = getBillCategory(info.row.original.type);
          return (
            <EtatBadge
              label={label.charAt(0).toUpperCase() + label.slice(1)}
              color={themeColor(color)}
            />
          );
        },
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.BILLS.COL.STATUS'}),
        accessorKey: 'isInvoiceOpen',
        cell: (info) => {
          const bill = info.row.original;
          if (bill.type === BillCategory.CREDIT) {
            return '-';
          }
          const status = billStatus(bill, paidLabel, unpaidLabel);
          return <EtatBadge label={status.label} color={status.color} />;
        },
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.BILLS.COL.AMOUNT_TAX_FREE'}),
        accessorKey: 'amountTaxFree',
        cell: (info) => formatAmount(info.row.original.amountTaxFree ?? undefined),
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.BILLS.COL.AMOUNT'}),
        accessorKey: 'amountIncludingTax',
        cell: (info) => formatAmount(info.row.original.amountIncludingTax),
      },
      {
        header: intl.formatMessage({id: 'BILLS.COL.DUE_DATE'}),
        accessorKey: 'dueDate',
        cell: (info) => {
          const {original: bill} = info.row;
          // TODO remove bill.type === 'INV' condition
          if (
            (bill.type === BillCategory.BILL || bill.type === 'INV') &&
            bill.dueDate !== '' &&
            bill.isInvoiceOpen !== false
          ) {
            const color = getDueDateColor(bill);
            return (
              <EtatBadge label={formatDate(bill.dueDate)} color={themeColor(color)} />
            );
          }
          return '-';
        },
      },
      {
        header: intl.formatMessage({id: 'TABLE.HEADER.ACTION'}),
        cell: (info) => (
          <>
            <DownloadBill bill={info.row.original} />
            <span
              role="button"
              style={{marginLeft: 10}}
              onClick={() => {
                navigate({
                  pathname: `/support`,
                  search: createSearchParams({
                    bill: info.row.original.id,
                  }).toString(),
                });
              }}
            >
              <OverlayTrigger
                overlay={
                  <Tooltip>
                    <span>{intl.formatMessage({id: 'BILLS.ACTION.MAKE_REQUEST'})}</span>
                  </Tooltip>
                }
                placement={'top'}
              >
                <i className="fs-1 fa-solid fa-file-circle-exclamation"></i>
              </OverlayTrigger>
            </span>
          </>
        ),
        enableSorting: false,
      },
    ],
    [navigate, intl, paidLabel, unpaidLabel]
  );

  const renderMobileCard = (bill: BillSearchResponse): JSX.Element => {
    const color = bill.type === BillCategory.BILL ? 'celeste-primary' : 'success';
    const typeLabel = getBillCategory(bill.type);
    const status = billStatus(bill, paidLabel, unpaidLabel);
    const showDueDate =
      (bill.type === BillCategory.BILL || bill.type === 'INV') &&
      bill.dueDate !== '' &&
      bill.isInvoiceOpen !== false;

    return (
      <div className={styles.mobileCardBody}>
        <div className={styles.mobileCardHeader}>
          <span className={styles.mobileCardId}>{bill.number}</span>
          <EtatBadge
            label={typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)}
            color={themeColor(color)}
          />
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'DASHBOARD.ORDERS.COL.DATE'})}
          </span>
          <span>{formatDate(bill.date)}</span>
        </div>
        {bill.type !== BillCategory.CREDIT && (
          <div className={styles.mobileCardRow}>
            <span className={styles.mobileCardLabel}>
              {intl.formatMessage({id: 'DASHBOARD.BILLS.COL.STATUS'})}
            </span>
            <EtatBadge label={status.label} color={status.color} />
          </div>
        )}
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'DASHBOARD.BILLS.COL.AMOUNT_TAX_FREE'})}
          </span>
          <span>{formatAmount(bill.amountTaxFree ?? undefined)}</span>
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'DASHBOARD.BILLS.COL.AMOUNT'})}
          </span>
          <span>{formatAmount(bill.amountIncludingTax)}</span>
        </div>
        {showDueDate && (
          <div className={styles.mobileCardRow}>
            <span className={styles.mobileCardLabel}>
              {intl.formatMessage({id: 'BILLS.COL.DUE_DATE'})}
            </span>
            <EtatBadge label={formatDate(bill.dueDate)} color={themeColor(getDueDateColor(bill))} />
          </div>
        )}
        <BillMobileActions bill={bill} />
      </div>
    );
  };

  const columnsWithoutAction = useMemo<ColumnDef<BillSearchResponse>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'BILLS.MODAL.COL.SELECT'}),
        accessorKey: 'id',
        cell: (info) => (
          
          <Form.Check
            type="checkbox"
            checked={selectedBills.includes(info.row.original.id)}
            onChange={(e) => {
              const billId = info.row.original.id;
              if (e.target.checked) {
                setSelectedBills((prev) => [...prev, billId]);
              } else {
                setSelectedBills((prev) => prev.filter((id) => id !== billId));
              }
            }}
          />

        ),
        enableSorting: false,
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.ORDERS.COL.DATE'}),
        accessorKey: 'date',
        cell: (info) => formatDate(info.row.original.date),
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.BILLS.COL.NUMBER'}),
        accessorKey: 'number',
        cell: (info) => info.row.original.number,
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.BILLS.COL.AMOUNT_TAX_FREE'}),
        accessorKey: 'amountTaxFree',
        cell: (info) => formatAmount(info.row.original.amountTaxFree ?? undefined),
      },
      {
        header: intl.formatMessage({id: 'DASHBOARD.BILLS.COL.AMOUNT'}),
        accessorKey: 'amountIncludingTax',
        cell: (info) => formatAmount(info.row.original.amountIncludingTax),
      },
    ],
    [selectedBills, intl]
  );

  const downloadSelectedBillsMutation = useMutation({
    mutationFn: async (selectedBills: string[]) => {
      const response = await axios.post(
        '/api/billing/bills/download-multiple',
        { salesInvoiceIds: selectedBills },
        { responseType: 'blob' } // Attendre un fichier blob
      );
  
      if (!response.data) {
        throw new Error('Erreur dans la réponse lors du téléchargement des factures.');
      }
  
      return response.data; // Retourne le fichier blob
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: 'application/zip' });
      saveAs(blob, 'factures.zip'); // Sauvegarde le fichier téléchargé
    },
    onError: (error) => {
      console.error('Erreur lors du téléchargement des factures :', error);
    },
  });
  

  const handleDownloadSelectedBills = () => {
    if (!selectedBills || selectedBills.length === 0) {
      console.warn('Aucune facture sélectionnée.');
      return;
    }
  
    downloadSelectedBillsMutation.mutate(selectedBills);
  };
  


  return (
    <>
      {/* Bouton "Télécharger plusieurs factures" */}
      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-primary btn-sm"
          onClick={(e) => {
            e.preventDefault();
            setShowModal(true);
          }}
        >
          {intl.formatMessage({id: 'ADMINISTRATIVE.BILL.DOWNLOAD_MULTIPLE_FILES'})}
        </button>
      </div>

      {/* Modal téléchargement multiple */}
      {showModal && (
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {intl.formatMessage({id: 'ADMINISTRATIVE.BILL.DOWNLOAD_MULTIPLE'})}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="pt-0">
            <TableMetronic
              data={bills ?? []}
              isLoading={isLoading}
              columns={columnsWithoutAction}
              pagination
              showCardHeader={false}
              searchBar={false}
              elementName={intl.formatMessage({id: 'ADMINISTRATIVE.BILL.ELEMENT'})}
              elementNamePlural={intl.formatMessage({id: 'ADMINISTRATIVE.BILL.ELEMENT_PLURAL'})}
              defaultSorting={[
                {
                  id: 'date',
                  desc: true,
                },
              ]}
            />
          </Modal.Body>

          <Modal.Footer>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={downloadSelectedBillsMutation.isLoading}>
              {intl.formatMessage({id: 'COMMON.CLOSE'})}
            </button>

            <button
              className="btn btn-primary"
              onClick={handleDownloadSelectedBills}
              disabled={selectedBills.length === 0 || downloadSelectedBillsMutation.isLoading}
            >
              {downloadSelectedBillsMutation.isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                </>
              ) : (
                intl.formatMessage({id: 'ADMINISTRATIVE.BILL.DOWNLOAD'})
              )}
            </button>

          </Modal.Footer>
        </Modal>
      )}




      {/* Table de factures */}
      {isError && (
        <div className="alert alert-warning" role="alert" style={{color: '#6b6b6b'}}>
          {intl.formatMessage({id: 'ADMINISTRATIVE.BILL.UNAVAILABLE'})}
        </div>
      )}
      <TableMetronic
        title={intl.formatMessage({id: 'ADMINISTRATIVE.BILL.MY_BILLS'})}
        data={filteredBills}
        isLoading={isLoading}
        columns={columns}
        renderMobileCard={renderMobileCard}
        pagination
        searchBar={true}
        stackedHeader
        toolbar={
          <>
            <div className={styles.filterSelect}>
              <ReactSelect<FilterOption<TypeFilter>>
                options={typeOptions}
                value={typeOptions.find((o) => o.value === typeFilter)}
                onChange={(opt) => setTypeFilter(opt?.value ?? 'all')}
                isSearchable={false}
                classNamePrefix="filter-select"
                menuPortalTarget={document.body}
                styles={filterSelectStyles}
              />
            </div>
            <div className={styles.filterSelect}>
              <ReactSelect<FilterOption<StatusFilter>>
                options={statusOptions}
                value={statusOptions.find((o) => o.value === statusFilter)}
                onChange={(opt) => setStatusFilter(opt?.value ?? 'all')}
                isSearchable={false}
                classNamePrefix="filter-select"
                menuPortalTarget={document.body}
                styles={filterSelectStyles}
              />
            </div>
          </>
        }
        elementName={intl.formatMessage({id: 'ADMINISTRATIVE.BILL.ELEMENT'})}
        elementNamePlural={intl.formatMessage({id: 'ADMINISTRATIVE.BILL.ELEMENT_PLURAL'})}
        defaultSorting={[
          {
            id: 'date',
            desc: true,
          },
        ]}
      />
    </>
  );
};

export default MyBills;
